<template>
  <!--
      triggered after selection change
      @event input
    -->
  <select
    class="text-sm leading-6"
    :class="[$style.select, { [$style.placeholder]: modelValue === '', [$style.error]: error }]"
    v-bind="$attrs"
    :value="modelValue"
    @input="handleInput"
  >
    <option v-if="placeholder !== ''" value="" disabled :selected="modelValue === ''">{{ placeholder }}</option>
    <option
      v-for="o in dropdownOptions"
      :key="o.value"
      :value="o.value"
      :disabled="o.disabled"
      :selected="modelValue === o.value"
    >
      {{ o.label }}
    </option>
  </select>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed } from 'vue'

export interface DropdownOption {
  value: string
  label: string
  disabled?: boolean
}

export default defineComponent({
  name: 'UiDropdown',
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    /**
     * selected value
     */
    modelValue: {
      type: String as PropType<string>,
      default: '',
    },
    /**
     * dropdown input placeholder
     */
    placeholder: {
      type: String as PropType<string>,
      default: '',
    },
    /**
     * item list
     */
    options: {
      type: Array as PropType<(DropdownOption | string)[]>,
      required: true,
    },
    /**
     * apply error styles
     */
    error: Boolean as PropType<boolean>,
  },
  setup(props, { emit }) {
    return {
      handleInput(event: Event) {
        if (event.target instanceof HTMLSelectElement) emit('update:modelValue', event.target.value)
      },
      dropdownOptions: computed(() => {
        return props.options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
      }),
    }
  },
})
</script>

<style lang="scss" module>
@mixin icon($color: '718096') {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.67 3.14l2.84 2.84c.3.3.09.83-.34.83H5.48a.48.48 0 01-.34-.83l2.84-2.84c.2-.19.5-.19.69 0zM8 12.86l-2.85-2.84a.48.48 0 01.35-.83h5.68c.43 0 .64.52.34.83l-2.84 2.84a.48.48 0 01-.68 0z' fill='%23#{$color}'/%3E%3C/svg%3E");
}

.select {
  appearance: none;
  outline: none;
  cursor: pointer;
  max-width: 368px;
  width: 100%;
  border-radius: var(--border-radius);
  padding: 7px 11px;
  padding-right: 32px;
  color: var(--gray-80);
  background-color: var(--gray-10);
  @include icon;
  background-position: calc(100% - 12px) 50%;
  background-size: 16px;
  background-repeat: no-repeat;
  border: solid 1px var(--gray-30);
  transition:
    border-color 0.16s ease-in,
    color 0.16s ease-in,
    box-shadow 0.16s ease-in;
  font-weight: theme('fontWeight.normal');

  &:hover {
    border-color: var(--gray-40);
  }

  &:focus {
    border-color: var(--active-primary);
    box-shadow: inset 0px 0px 2px rgba(59, 76, 191, 0.6);
  }

  &:disabled {
    cursor: not-allowed;
    @include icon('CBD5E0');
    color: var(--gray-50);
    border-color: var(--gray-20);
    background-color: var(--gray-20);
  }
}

.placeholder {
  color: var(--gray-50);
}

.error {
  background-color: var(--red-10);
  border-color: var(--red-40);

  &:focus {
    border-color: var(--red-50);
    box-shadow: inset 0px 0px 4px rgba(229, 62, 62, 0.6);
  }
}
</style>
